import { PlaceOutlined } from '@mui/icons-material';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import DialogCloseButton from '../../common/components/DialogCloseButton';
import { UserRoleDocument } from '../../queries/__generated__/userRole.generated';
import { currentContextAtom } from '../../state';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { AdministrativeArea } from '../../hooks/useCurrentUser';
import { useInsertRoleAssignmentAdministrativeAreasMutation } from '../../mutations/__generated__/insertRoleAssignmentAdministrativeAreas.generated';
import SelectableList from '../../common/components/SelectableList';
import { SettingsDialogViewProps } from './SettingsDialog';

export interface AdministrativeAreasSettingsProps extends SettingsDialogViewProps {
  areas: AdministrativeArea[];
  currentAreas?: AdministrativeArea[];
}

export default function AdministrativeAreasSettings({
  next,
  standAlone,
  areas,
  currentAreas,
}: AdministrativeAreasSettingsProps): React.JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const context = useAtomValue(currentContextAtom);
  const [selectedAreas, setSelectedAreas] = useState<AdministrativeArea[]>(currentAreas || []);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [insertRoleAssignmentAdministrativeAreas, { data, loading }] = useInsertRoleAssignmentAdministrativeAreasMutation({
    refetchQueries: [UserRoleDocument],
    awaitRefetchQueries: true,
  });

  const isAllSelected = selectedAreas.length === areas?.length;

  const handleSubmit = (): void => {
    insertRoleAssignmentAdministrativeAreas({
      variables: {
        role_assignment_id: context.roleAssignmentId,
        roleAssignmentAdministrativeAreas: !isAllSelected
          ? selectedAreas.map((a) => {
            return {
              role_assignment_id: context.roleAssignmentId,
              area: a.area,
              country_code: a.country_code,
            };
          })
          : {
            role_assignment_id: context.roleAssignmentId,
            has_all_areas: true,
          },
      },
    });
  };

  useEffect((): void => {
    if (data?.insert_role_assignment_administrative_area?.affected_rows
      || data?.delete_role_assignment_administrative_area?.affected_rows) {
      setCommonSnackbarProps({
        label: t('settings:your_profile_has_been_updated'),
        snackbarProps: {
          open: true,
        },
      });

      next({ skipped: !!standAlone });
    }
  }, [data]);

  const isValidationDisabled = useMemo(() => {
    if (currentAreas && selectedAreas.length !== currentAreas?.length) {
      return false;
    }

    const selectedValues = selectedAreas.map((a) => a.area).sort();
    const currentValues = (currentAreas ?? []).map((a) => a.area).sort();

    return selectedValues.every((v, i) => v === currentValues[i]);
  }, [selectedAreas]);

  return (
    <>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex'
          }}
        >
          <PlaceOutlined />
          <Typography sx={{ flex: 1 }}>
            {t('settings:your_administrative_area_level_2')}
          </Typography>
        </Box>
        <Typography>{t('settings:your_administrative_area_level_2_description')}</Typography>
      </DialogTitle>
      {standAlone && (
        <DialogCloseButton onClick={() => next()} />
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: 0,
          gap: 1.5,

        }}
      >
        <SelectableList
          items={areas}
          selected={selectedAreas}
          getKey={({ area }) => area!}
          getLabel={({ area }) => area!}
          onChange={setSelectedAreas}
        />
      </DialogContent>
      <DialogActions>
        {standAlone
          ? (
            <Button onClick={() => next()} variant="outlined">
              {t('common:cancel')}
            </Button>
          )
          : (
            <Button onClick={() => next({ skipped: true })} variant="outlined">
              {t('settings:complete_later')}
            </Button>
          )}
        <Button
          disabled={isValidationDisabled}
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
        >
          {t('common:validate')}
        </Button>
      </DialogActions>
    </>
  );
}
